import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Tag } from "lucide-react";
import api from "@/lib/api";
import MediaCarousel from "@/components/MediaCarousel";
import { DynIcon } from "@/lib/icons";
import { useStore } from "@/context/StoreContext";
import { effectivePrice, unitDiscount, nextTierHint, money, DEFAULT_TIERS, productVariants, unitPriceFor } from "@/lib/pricing";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useStore();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      const vs = productVariants(data);
      setVariant(vs[0] || null);
    }).catch(() => setProduct(false));
  }, [id]);

  if (product === false) return <div className="p-8 text-center text-[#A8A2A0]">Product not found.</div>;
  if (!product) return <div className="p-8 text-center text-[#A8A2A0]">Loading...</div>;

  const variants = productVariants(product);
  const price = unitPriceFor(product, variant);
  const onSale = variants.length === 0 && product.hot_steal && product.sale_price != null;
  const tiers = product.tiers && product.tiers.length ? product.tiers : DEFAULT_TIERS;
  const ud = unitDiscount(qty, tiers);
  const lineTotal = price * qty - ud * qty;
  const hint = nextTierHint(qty, tiers);
  const soldOut = product.stock === "Sold Out";

  return (
    <div className="px-4 py-6" data-testid="product-detail">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#A8A2A0] hover:text-[#E60000] mb-4" data-testid="back-btn">
        <ArrowLeft size={20} /> Back
      </button>

      <div className="rounded-2xl overflow-hidden border border-[#333]">
        <MediaCarousel media={product.media} heightClass="aspect-[4/3]" contain testid="detail-carousel" />
      </div>

      <div className="mt-5">
        <div className="flex items-start justify-between gap-3">
          <h1 className="font-heading font-black uppercase text-2xl text-[#F0EDEA]">{product.name} <span className="text-[#A8A2A0] font-normal text-lg">#{product.code}</span></h1>
          {product.stock && <span className="text-xs font-semibold px-2 py-1 rounded bg-[#141414] border border-[#333] text-[#F0EDEA]">{product.stock}</span>}
        </div>

        <div className="mt-3">
          <div className="eyebrow text-xs">Price</div>
          <div className="flex items-baseline gap-3">
            <span className="font-heading font-black text-4xl text-[#E60000]">{money(price)}</span>
            {onSale && <span className="text-[#A8A2A0] line-through">{money(product.price)}</span>}
          </div>
        </div>

        {variants.length > 0 && (
          <div className="mt-4">
            <div className="eyebrow text-xs mb-2">Size</div>
            <div className="flex flex-wrap gap-2" data-testid="detail-variants">
              {variants.map((v) => (
                <button
                  key={v.label}
                  onClick={() => setVariant(v)}
                  data-testid={`detail-variant-${v.label}`}
                  className={`font-heading font-bold uppercase text-sm px-4 py-2 rounded-full border transition-colors ${variant?.label === v.label ? "border-[#E60000] bg-[#E60000] text-white" : "border-[#333] text-[#A8A2A0]"}`}
                >
                  {v.label} · {money(v.price)}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 bg-[#141414] border border-[#2a2a2a] rounded-xl p-4">
          <div className="eyebrow text-xs text-[#E60000] mb-2">Bundle Up & Save</div>
          {tiers.map((t, i) => (
            <div key={i} className="flex justify-between text-sm text-[#A8A2A0] py-0.5">
              <span>{t.units}+ Units</span><span className="text-[#F0EDEA] font-semibold">{money(t.discount)} OFF each</span>
            </div>
          ))}
        </div>

        {product.badges?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {product.badges.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 bg-[#141414] border border-[#2a2a2a] rounded-full px-3 py-1.5 text-sm text-[#F0EDEA]">
                <DynIcon name={b.icon} size={14} className="text-[#E60000]" /> {b.label}
              </span>
            ))}
          </div>
        )}

        {product.profile_nose && <p className="mt-4 text-sm text-[#F0EDEA]"><span className="font-semibold">Profile/Nose:</span> <span className="text-[#A8A2A0]">{product.profile_nose}</span></p>}
        {product.summary && <p className="mt-2 text-sm text-[#A8A2A0]"><span className="text-[#F0EDEA] font-semibold">Summary:</span> {product.summary}</p>}

        {/* Qty selector + live total */}
        <div className="mt-6 bg-[#1C1C1C] border border-[#333] rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center border border-[#333] rounded-xl overflow-hidden">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-3 text-[#F0EDEA] hover:bg-[#E60000]" data-testid="detail-qty-minus"><Minus size={16} /></button>
              <span className="px-5 font-heading font-black text-[#F0EDEA] text-lg" data-testid="detail-qty">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-4 py-3 text-[#F0EDEA] hover:bg-[#E60000]" data-testid="detail-qty-plus"><Plus size={16} /></button>
            </div>
            <div className="text-right">
              <div className="eyebrow text-[10px]">Total</div>
              <div className="font-heading font-black text-2xl text-[#E60000]" data-testid="detail-total">{money(lineTotal)}</div>
            </div>
          </div>
          {hint && (
            <div className="mt-3 flex items-center gap-2 text-[#FF4550] text-xs bg-[#1a0d0d] border border-[#3a1414] rounded-lg px-3 py-2">
              <Tag size={14} /> Add {hint.add} more to save {money(hint.save)} per unit
            </div>
          )}
          <button disabled={soldOut} onClick={() => addToCart(product, qty, variant)} className="btn-skew btn-primary w-full mt-4" data-testid="detail-add-btn">
            <span>{soldOut ? "Sold Out" : "Add to List"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
